/**
 * Widget Registry - Maps widget IDs to their components and metadata.
 * Used by the customizable dashboard to render and manage widgets.
 */

import { lazy, ComponentType } from 'react';

// Widget metadata interface
export interface WidgetDefinition {
  id: string;
  name: string;
  description: string;
  icon: string; // Lucide icon name
  component: ComponentType<Record<string, never>>;
  defaultVisible: boolean;
  fullWidth?: boolean; // If true, spans 2 columns on desktop
}

// Lazy load widgets for code splitting
const CopilotChatWidget = lazy(() =>
  import('./CopilotChatWidget').then(m => ({ default: m.CopilotChatWidget }))
);
const PortfolioInsightsWidget = lazy(() =>
  import('./PortfolioInsightsWidget').then(m => ({ default: m.PortfolioInsightsWidget }))
);
const AllocationOverviewWidget = lazy(() =>
  import('./AllocationOverviewWidget').then(m => ({ default: m.AllocationOverviewWidget }))
);
const PerformanceAttributionWidget = lazy(() =>
  import('./PerformanceAttributionWidget').then(m => ({ default: m.PerformanceAttributionWidget }))
);
const StalenessWidget = lazy(() =>
  import('./StalenessWidget').then(m => ({ default: m.StalenessWidget }))
);
const RecentActivityWidget = lazy(() =>
  import('./RecentActivityWidget').then(m => ({ default: m.RecentActivityWidget }))
);
const TopHoldingsWidget = lazy(() =>
  import('./TopHoldingsWidget').then(m => ({ default: m.TopHoldingsWidget }))
);
const DealPipelineWidget = lazy(() =>
  import('./DealPipelineWidget').then(m => ({ default: m.DealPipelineWidget }))
);
const AttentionItemsWidget = lazy(() =>
  import('./AttentionItemsWidget').then(m => ({ default: m.AttentionItemsWidget }))
);
const AgentCommandCenterWidget = lazy(() =>
  import('./AgentCommandCenterWidget').then(m => ({ default: m.AgentCommandCenterWidget }))
);
const CapitalEventsWidget = lazy(() =>
  import('./CapitalEventsWidget').then(m => ({ default: m.CapitalEventsWidget }))
);

// Widget registry mapping IDs to definitions
export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {
  'copilot-chat': {
    id: 'copilot-chat',
    name: 'Copilot',
    description: 'AI assistant for portfolio questions and analysis',
    icon: 'Bot',
    component: CopilotChatWidget,
    defaultVisible: true,
    fullWidth: true,
  },
  'portfolio-insights': {
    id: 'portfolio-insights',
    name: 'Portfolio Insights',
    description: 'AI-detected portfolio gaps and opportunities',
    icon: 'Lightbulb',
    component: PortfolioInsightsWidget,
    defaultVisible: true,
    fullWidth: true,
  },
  'allocation-overview': {
    id: 'allocation-overview',
    name: 'Asset Allocation',
    description: 'Portfolio allocation breakdown by asset class',
    icon: 'PieChart',
    component: AllocationOverviewWidget,
    defaultVisible: true,
  },
  'performance-attribution': {
    id: 'performance-attribution',
    name: 'Performance Attribution',
    description: 'Return attribution by investment type',
    icon: 'TrendingUp',
    component: PerformanceAttributionWidget,
    defaultVisible: true,
  },
  'staleness': {
    id: 'staleness',
    name: 'Private Fund Staleness',
    description: 'Funds with NAV marks older than 30 days',
    icon: 'Clock',
    component: StalenessWidget,
    defaultVisible: true,
  },
  'recent-activity': {
    id: 'recent-activity',
    name: 'Recent Activity',
    description: 'Latest documents, meetings, and events',
    icon: 'Activity',
    component: RecentActivityWidget,
    defaultVisible: true,
  },
  'top-holdings': {
    id: 'top-holdings',
    name: 'Top Holdings',
    description: 'Largest positions by market value',
    icon: 'Trophy',
    component: TopHoldingsWidget,
    defaultVisible: false,
  },
  'deal-pipeline': {
    id: 'deal-pipeline',
    name: 'Deal Pipeline',
    description: 'Active investment opportunities',
    icon: 'GitBranch',
    component: DealPipelineWidget,
    defaultVisible: false,
  },
  'attention-items': {
    id: 'attention-items',
    name: 'Attention Items',
    description: 'Items requiring immediate action',
    icon: 'AlertTriangle',
    component: AttentionItemsWidget,
    defaultVisible: false,
  },
  'agent-command-center': {
    id: 'agent-command-center',
    name: 'Agent Command Center',
    description: 'AI agent task monitoring and control',
    icon: 'Cpu',
    component: AgentCommandCenterWidget,
    defaultVisible: false,
  },
  'capital-events': {
    id: 'capital-events',
    name: 'Capital Calls',
    description: 'Upcoming capital calls for next 90 days',
    icon: 'ArrowUpRight',
    component: CapitalEventsWidget,
    defaultVisible: true,
  },
};

// Default widget order for new users/orgs
export const DEFAULT_WIDGET_ORDER = [
  'copilot-chat',
  'portfolio-insights',
  'allocation-overview',
  'performance-attribution',
  'capital-events',
  'staleness',
  'recent-activity',
];

// Default hidden widgets
export const DEFAULT_HIDDEN_WIDGETS = [
  'top-holdings',
  'deal-pipeline',
  'attention-items',
  'agent-command-center',
];

// Helper to get visible widgets in order
export function getVisibleWidgets(
  widgetOrder: string[],
  hiddenWidgets: string[]
): WidgetDefinition[] {
  return widgetOrder
    .filter(id => !hiddenWidgets.includes(id) && WIDGET_REGISTRY[id])
    .map(id => WIDGET_REGISTRY[id]);
}

// Helper to get all widget definitions as array
export function getAllWidgets(): WidgetDefinition[] {
  return Object.values(WIDGET_REGISTRY);
}
